import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CreditCard, ExternalLink, ShieldCheck, Unlink } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { Button, Card } from '../ui/kit'
import { oeffneExtern } from '../lib/extern'

/**
 * Guest payments through the landlord's own Stripe account.
 *
 * Connect **Standard**: they hold the account, Stripe checks them directly, they are
 * the merchant of record. AUVISO never touches the money and never becomes a payment
 * facilitator — which was the deciding requirement, not a technical preference.
 *
 * This card is deliberately blunt about that. A landlord about to enter bank details
 * should be able to read, in one paragraph, whose account it is and who gets paid.
 */

interface ConnectStatus {
  connected: boolean
  stripe_account_id: string | null
  charges_enabled: boolean
  can_connect: boolean
  configured: boolean
}

async function call(action: string, extra: Record<string, unknown> = {}) {
  if (!supabase) throw new Error('no_backend')
  const { data, error } = await supabase.functions.invoke('stripe-connect', {
    body: { action, ...extra },
  })
  if (error) {
    const res = (error as { context?: Response }).context
    if (res && typeof res.json === 'function') {
      const body = await res.json().catch(() => null) as
        { error?: string; hint?: string; detail?: string } | null
      // Den Klartext von Stripe mitnehmen: „onboarding_failed" allein hat uns einen
      // halben Tag gekostet, weil niemand sehen konnte, was dahinter stand.
      if (body?.error) throw new Error(body.detail ? `${body.error}: ${body.detail}` : body.error)
    }
    throw new Error(error.message)
  }
  return data as Record<string, unknown>
}

export default function StripeConnectCard() {
  const { lang } = useI18n()
  const store = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const de = lang === 'de'

  const [status, setStatus] = useState<ConnectStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!store.live) return
    try { setStatus(await call('status') as unknown as ConnectStatus) } catch { /* card stays quiet */ }
  }, [store.live])

  useEffect(() => { void load() }, [load])

  // Two ways back, because there are two ways out. OAuth returns ?code=&state=;
  // Stripe's hosted onboarding just returns, so we ask the account what it can do
  // rather than trusting the fact that the browser arrived.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const code = params.get('code')
    const state = params.get('state')
    const returned = params.get('done') || params.get('refresh')

    if (returned && !code) {
      setBusy(true)
      void (async () => {
        try { await call('refresh'); await load() }
        catch (e) { setError(message(String((e as Error).message), de)) }
        setBusy(false)
        navigate('/settings', { replace: true })
      })()
      return
    }
    if (!code || !state) return
    setBusy(true)
    void (async () => {
      try {
        await call('callback', { code, state })
        await load()
      } catch (e) {
        setError(message(String((e as Error).message), de))
      }
      setBusy(false)
      navigate('/settings', { replace: true })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  if (!store.live) return null

  const connect = async () => {
    setBusy(true); setError(null)
    try {
      const { url } = await call('link') as { url: string }
      // In der Hülle kehrt der Aufruf beim Schließen zurück; dann muss der
      // Status neu geholt werden, sonst zeigt die Karte weiter „nicht
      // verbunden", obwohl die Einrichtung durchlief.
      await oeffneExtern(url)
      await load()
      setBusy(false)
    } catch (e) {
      setError(message(String((e as Error).message), de))
      setBusy(false)
    }
  }

  const disconnect = async () => {
    if (!window.confirm(de
      ? 'Verbindung zu Stripe trennen? Bereits erhaltene Zahlungen bleiben davon unberührt.'
      : 'Disconnect Stripe? Payments already received are unaffected.')) return
    setBusy(true); setError(null)
    try { await call('disconnect'); await load() } catch (e) {
      setError(message(String((e as Error).message), de))
    }
    setBusy(false)
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <CreditCard size={18} /> {de ? 'Gästezahlungen (Stripe)' : 'Guest payments (Stripe)'}
      </h2>

      <p className="text-sm text-muted">
        {de
          ? 'Ihre Gäste zahlen direkt auf Ihr eigenes Stripe-Konto. Das Geld geht nie über AUVISO — Sie sind Vertragspartner von Stripe, die Auszahlung läuft auf Ihr Bankkonto.'
          : 'Your guests pay straight into your own Stripe account. The money never passes through AUVISO — your contract is with Stripe and payouts go to your bank account.'}
      </p>

      {status?.connected ? (
        <>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck size={16} className="text-ok" />
            <span className="font-medium">
              {de ? 'Verbunden' : 'Connected'}
            </span>
            <span className="text-muted tabular-nums">{status.stripe_account_id}</span>
          </div>
          {!status.charges_enabled && (
            <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
              {de
                ? 'Stripe prüft Ihr Konto noch. Zahlungslinks funktionieren, sobald Stripe es freigegeben hat.'
                : 'Stripe is still reviewing your account. Payment links work once they have enabled it.'}
            </p>
          )}
          <Button variant="secondary" size="sm" disabled={busy} onClick={disconnect}>
            <Unlink size={15} /> {de ? 'Verbindung trennen' : 'Disconnect'}
          </Button>
        </>
      ) : (
        <>
          {status && !status.configured && (
            <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
              {de
                ? 'Gästezahlungen sind noch nicht freigeschaltet. Bitte AUVISO kontaktieren.'
                : 'Guest payments are not switched on yet. Please contact AUVISO.'}
            </p>
          )}
          {status?.configured && (
            <p className="text-xs text-muted">
              {de
                ? 'Dauert etwa fünf Minuten: Firmendaten, Ausweis und Bankverbindung — alles direkt bei Stripe.'
                : 'About five minutes: company details, ID and bank account — all handled by Stripe.'}
            </p>
          )}
          <Button
            variant="primary"
            disabled={busy || !status?.can_connect}
            onClick={connect}
          >
            <ExternalLink size={16} />
            {busy
              ? (de ? 'Weiterleitung zu Stripe …' : 'Redirecting to Stripe …')
              : (de ? 'Mit Stripe verbinden' : 'Connect with Stripe')}
          </Button>
          <p className="text-xs text-muted">
            {de
              ? 'Sie werden zu Stripe weitergeleitet und melden sich dort an oder legen ein Konto an. StaySuite erhält danach nur die Erlaubnis, Zahlungen für Ihre Rechnungen anzulegen.'
              : 'You are sent to Stripe to sign in or create an account. StaySuite is then permitted to create payments for your invoices — nothing more.'}
          </p>
        </>
      )}

      {error && <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>}
    </Card>
  )
}

function message(code: string, de: boolean): string {
  switch (code) {
    case 'role_may_not_connect':
      return de ? 'Nur die Inhaberin oder der Inhaber kann Stripe verbinden.' : 'Only the owner can connect Stripe.'
    case 'connect_not_configured':
      return de ? 'Gästezahlungen sind noch nicht freigeschaltet.' : 'Guest payments are not switched on yet.'
    case 'invalid_state':
      return de
        ? 'Die Verbindung ist abgelaufen. Bitte erneut starten.'
        : 'That connection attempt expired. Please start again.'
    case 'oauth_exchange_failed':
      return de ? 'Stripe hat die Verbindung abgelehnt.' : 'Stripe refused the connection.'
    default:
      // Enthält bei Stripe-Fehlern den Originaltext hinter dem Doppelpunkt.
      return de ? `Es hat nicht geklappt: ${code}` : `That did not work: ${code}`
  }
}
